// components/SessionGuard.jsx
// The one place in the app that turns an expired session into a navigation.
//
// api/client.js no longer touches window.location: it throws on every 401 and,
// for the calls that opt in with `authRequired`, asks this component to move the
// user. Living inside the Router means the redirect is a history push rather
// than a document reload, and that it can read the URL the user was actually on
// so login has somewhere to send them back to.
//
// Renders nothing.
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setUnauthorizedHandler } from "../api/client";
import { currentPath, loginUrlFor } from "../lib/nextUrl";

export default function SessionGuard() {
  const navigate = useNavigate();
  const location = useLocation();

  // The handler is registered once, but it has to see the current location when
  // it fires, not the one from the render that registered it. A ref keeps it
  // current without re-registering on every navigation.
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    setUnauthorizedHandler(() => {
      const location = locationRef.current;
      // Already there: a 401 from the login page itself must not bounce it.
      if (location.pathname === "/login") return;
      // replace, not push: this is a redirect caused by state, so pressing back
      // must not land on the page that will only redirect again.
      navigate(loginUrlFor(currentPath(location)), { replace: true });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate]);

  return null;
}
